import { translitToRus } from 'app/utils';
import { createSelector } from 'reselect';

const transportState = (state) => state.transport;

export const itemsSelector = createSelector(transportState, (transport) => transport.items);
export const selectedItemSelector = createSelector(transportState, (transport) => transport.selectedItem);
export const checkedItemsSelector = createSelector(transportState, (transport) => transport.checkedItems);
export const searchValueSelector = createSelector(transportState, (transport) => transport.searchValue);
export const inspectionSelectedItemSelector = createSelector(transportState, (transport) => transport.inspectionSelectedItem);

export const groupItemsSelector = createSelector(transportState, (transport) => transport.groupItems);
export const groupSelectedItemSelector = createSelector(transportState, (transport) => transport.groupSelectedItem);
export const groupSearchValueSelector = createSelector(transportState, (transport) => transport.groupSearchValue);
export const checkListSelectedItemSelector = createSelector(transportState, (transport) => transport.checkListSelectedItem);

export const sortSelectedItemSelector = createSelector(transportState, (transport) => transport.sortSelectedItem);
export const sortItemsSelector = createSelector([itemsSelector, sortSelectedItemSelector, searchValueSelector], (items, sortSelectedItem, searchValue) => {
    const compareFn = (a, b) => {
        switch (sortSelectedItem.name) {
            case 'name':
                switch (sortSelectedItem.order) {
                    case 'ASC':
                        return a.name.localeCompare(b.name, {
                            sensitivity: 'base',
                        });
                    case 'DESC':
                        return b.name.localeCompare(a.name, {
                            sensitivity: 'base',
                        });
                }
                break;
            case 'group_name':
                switch (sortSelectedItem.order) {
                    case 'ASC':
                        return b.group_name === null
                            ? -1
                            : a.group_name === null
                              ? 1
                              : a.group_name.localeCompare(b.group_name, {
                                    sensitivity: 'base',
                                });
                    case 'DESC':
                        return b.group_name === null
                            ? -1
                            : a.group_name === null
                              ? 1
                              : b.group_name.localeCompare(a.group_name, {
                                    sensitivity: 'base',
                                });
                }
                break;
            case 'imei':
                switch (sortSelectedItem.order) {
                    case 'ASC':
                        return b.imei === null
                            ? -1
                            : a.imei === null
                              ? 1
                              : a.imei.localeCompare(b.imei, {
                                    sensitivity: 'base',
                                });
                    case 'DESC':
                        return b.imei === null
                            ? -1
                            : a.imei === null
                              ? 1
                              : b.imei.localeCompare(a.imei, {
                                    sensitivity: 'base',
                                });
                }
                break;
            default:
                switch (typeof a[sortSelectedItem.name]) {
                    case 'string':
                        switch (sortSelectedItem.order) {
                            case 'ASC':
                                return b.number === null
                                    ? -1
                                    : a.number === null
                                      ? 1
                                      : a.number.localeCompare(b.number, {
                                            sensitivity: 'base',
                                        });
                            case 'DESC':
                                return b.number === null
                                    ? -1
                                    : a.number === null
                                      ? 1
                                      : b.number.localeCompare(a.number, {
                                            sensitivity: 'base',
                                        });
                        }
                        break;
                    case 'number':
                        switch (sortSelectedItem.order) {
                            case 'ASC':
                                return a[sortSelectedItem.name] - b[sortSelectedItem.name];
                            case 'DESC':
                                return b[sortSelectedItem.name] - a[sortSelectedItem.name];
                        }
                        break;
                }
        }
    };

    if (searchValue.length) {
        switch (sortSelectedItem.name) {
            case 'imei':
                return items.filter((item) => (item.imei || '').toLowerCase().includes(searchValue.toLowerCase()));
            case 'name':
                return items
                    .filter((item) =>
                        translitToRus(item.name || '')
                            .toLowerCase()
                            .includes(translitToRus(searchValue).toLowerCase())
                    )
                    .sort(compareFn);
            case 'group_name':
                return items
                    .filter((item) =>
                        translitToRus(item.group_name || '')
                            .toLowerCase()
                            .includes(translitToRus(searchValue).toLowerCase())
                    )
                    .sort(compareFn);
            case 'number':
                return items
                    .filter((item) =>
                        translitToRus(item.number || '')
                            .toLowerCase()
                            .includes(translitToRus(searchValue).toLowerCase())
                    )
                    .sort(compareFn);
            case 'volume':
                return items.filter((item) => (item.volume || '').toString().includes(searchValue)).sort(compareFn);
            default:
                return items.sort(compareFn);
        }
    }
    return items.sort(compareFn);
});

export const sortItemsGroupSelector = createSelector([groupItemsSelector, groupSearchValueSelector], (groupItems, groupSearchValue) => {
    const compareFn = (a, b) => a.name.localeCompare(b.name, { sensitivity: 'base' });

    if (groupSearchValue.length) {
        return groupItems.filter((item) => translitToRus(item.name).indexOf(translitToRus(groupSearchValue))).sort(compareFn);
    }

    return groupItems.sort(compareFn);
});

export const divisionItemsTransportSelector = createSelector(transportState, (transport) => transport.divisionItems);
export const autocolonItemsTransportSelector = createSelector(transportState, (transport) => transport.autocolonItems);
export const autocolonNotSelectedItemsTransportSelector = createSelector(transportState, (transport) => transport.autocolonItems.filter((item) => !item.id_division));
export const autocolonSearchValueTransportSelector = createSelector(transportState, (transport) => {
    return transport.autocolonSearchValue;
});
export const autocolonSelectedItemTransportSelector = createSelector(transportState, (transport) => transport.autocolonSelectedItem);
export const autocolonGeofenceCheckedIdTransportSelector = createSelector(transportState, (transport) => transport?.autocolonSelectedItem?.geofenceItems || []);
export const divisionAutocolnSortedItemsTransportSelector = createSelector([divisionItemsTransportSelector, autocolonItemsTransportSelector, transportState], (divisionItems, autocolonItems, transport) => {
    const autocolonSearchValue = transport.autocolonSearchValue;
    if (autocolonSearchValue) {
        return divisionItems
            .sort((a, b) =>
                a.name.localeCompare(b.name, {
                    sensitivity: 'base',
                })
            )
            .map((item) => {
                let autocolons = autocolonItems
                    .filter((autocolonItem) => autocolonItem.id_division === item.id)
                    .sort((a, b) => a.name - b.name)
                    .filter((autocolonItem) => {
                        return autocolonItem.name.toLowerCase().match(autocolonSearchValue.toLowerCase() || '');
                    });
                item.autocolons = autocolons;
                return item;
            })
            .concat({
                name: 'Не распределено',
                id: 0,
                autocolons: autocolonItems
                    .filter((autocolonItem) => !autocolonItem.id_division)
                    .sort((a, b) =>
                        a.name.localeCompare(b.name, {
                            sensitivity: 'base',
                        })
                    )
                    .filter((autocolonItem) => autocolonItem.name.toLowerCase().match(autocolonSearchValue.toLowerCase() || '')),
            });
    }
    const res = divisionItems
        .sort((a, b) =>
            a.name.localeCompare(b.name, {
                sensitivity: 'base',
            })
        )
        .map((item) => {
            let autocolons = autocolonItems
                .filter((autocolonItem) => autocolonItem.id_division === item.id)
                .sort((a, b) =>
                    a.name.localeCompare(b.name, {
                        sensitivity: 'base',
                    })
                );
            item.autocolons = autocolons;
            return item;
        })
        .concat({
            name: 'Не распределено',
            id: 0,
            autocolons: autocolonItems.filter((autocolonItem) => !autocolonItem.id_division).sort((a, b) => a.name - b.name),
        });
    return res;
});
